import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import { themes } from '../../theme.ts';

export interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  show: boolean;
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  show,
  onClose,
  duration = 3000
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const { themeName } = useTheme();

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300); // Allow animation to complete before removing
      }, duration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [show, onClose, duration]);

  const getToastStyles = () => {
    const baseStyles = {
      backgroundColor: themes[themeName].colors.card,
      color: themes[themeName].colors.text,
      borderLeftColor: themes[themeName].colors[type === 'success' ? 'success' : type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'primary'],
      borderLeftWidth: '4px',
      borderLeftStyle: 'solid' as const,
    };
    return baseStyles;
  };

  const getIconColor = () => {
    return themes[themeName].colors[type === 'success' ? 'success' : type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'primary'];
  };

  const getIcon = () => {
    const iconStyle = { color: getIconColor() };
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5" style={iconStyle} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5" style={iconStyle} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5" style={iconStyle} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5" style={iconStyle} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd"></path>
          </svg>
        );
    }
  };

  if (!show && !isVisible) return null;

  return (
    <div className="fixed bottom-5 z-[995] right-5">
      <div
        className={`
          flex items-center p-4 mb-4 max-w-xs rounded-lg shadow
          transition-all duration-300 transform
          ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}
        `}
        style={getToastStyles()}
        role="alert"
      >
        <div className="inline-flex flex-shrink-0 justify-center items-center mr-3">
          {getIcon()}
        </div>
        <div className="ml-3 text-sm font-normal">{message}</div>
        <button
          type="button"
          className="ml-auto -mx-1.5 -my-1.5 rounded-lg focus:ring-2 p-1.5 inline-flex h-8 w-8"
          style={{
            color: themes[themeName].colors.textSecondary,
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themes[themeName].colors.border}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
        >
          <span className="sr-only">Close</span>
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Toast;
